import { t } from "@/lib/i18n/interpolate";
import {
  type DocBlock,
  type DocListItem,
  type DocRun,
  type DocSection,
  type DocumentModel,
  toRoman,
} from "@/lib/tool-kit/document";
import { fieldOrPrompt } from "@/lib/tool-kit/essay-templates/text";

/**
 * A full-sentence speech outline in the format public-speaking courses teach: topic,
 * specific purpose and central idea at the top, then Introduction, Body and Conclusion,
 * each numbered with Roman numerals from I, sub-points lettered A, B, C and details
 * numbered 1, 2, 3, with a labelled transition between main points.
 *
 * A persuasive speech can instead follow Monroe's Motivated Sequence (Alan H. Monroe,
 * 1935): Attention opens the introduction, Need, Satisfaction and Visualization are the
 * three main points of the body, and Action closes the conclusion.
 */

export type SpeechType = "informative" | "persuasive";
export type SpeechPattern = "topical" | "monroe";

export interface SpeechPoint {
  main: string;
  /** One sub-point per line; an indented or "-" line is a detail of the one above. */
  subs: string;
  transition: string;
}

export interface SpeechOutlineInput {
  type: SpeechType;
  pattern: SpeechPattern;
  topic: string;
  purpose: string;
  central: string;
  attention: string;
  credibility: string;
  preview: string;
  points: SpeechPoint[];
  action: string;
  summary: string;
  close: string;
}

export const MONROE_BODY_STEPS = [
  "need",
  "satisfaction",
  "visualization",
] as const;
export type MonroeBodyStep = (typeof MONROE_BODY_STEPS)[number];

export interface SpeechOutlineLabels {
  defaultTitle: string;
  subtitles: { informative: string; persuasive: string; monroe: string };
  fields: { topic: string; purpose: string; central: string };
  headings: {
    introduction: string;
    introductionMonroe: string;
    body: string;
    conclusion: string;
    conclusionMonroe: string;
    references: string;
  };
  items: {
    attention: string;
    credibility: string;
    preview: string;
    summary: string;
    action: string;
    close: string;
    transition: string;
  };
  monroeSteps: Record<MonroeBodyStep, string>;
  prompts: {
    topic: string;
    purposeInformative: string;
    purposePersuasive: string;
    central: string;
    attention: string;
    credibility: string;
    preview: string;
    previewWithPoints: string;
    mainPoint: string;
    support: string;
    transition: string;
    summary: string;
    summaryWithPoints: string;
    action: string;
    close: string;
    references: string;
  };
  monroePrompts: Record<MonroeBodyStep, string>;
  referencesNote: string;
}

const BLANK_POINT: SpeechPoint = { main: "", subs: "", transition: "" };

export interface SubPoint {
  text: string;
  details: string[];
}

const LINE_BREAK = /\r?\n/;
/** An indented line, or one opened with a dash or bullet, is a detail. */
const DETAIL_LINE = /^(?:\s+\S|\s*[-*•–]\s)/;
/** Markers a writer might type: "- ", "• ", "A. ", "a) ", "1. ". */
const OUTLINE_PREFIX = /^(?:[-*•–—·]|\d+[.)]|[A-Za-z][.)])\s+/;

export function parseSubPoints(text: string): SubPoint[] {
  const points: SubPoint[] = [];
  for (const line of text.split(LINE_BREAK)) {
    const clean = line.trim().replace(OUTLINE_PREFIX, "").trim();
    if (clean === "") {
      continue;
    }
    const parent = points.at(-1);
    if (parent && DETAIL_LINE.test(line)) {
      parent.details.push(clean);
    } else {
      points.push({ text: clean, details: [] });
    }
  }
  return points;
}

export function isMonroe(input: SpeechOutlineInput): boolean {
  return input.type === "persuasive" && input.pattern === "monroe";
}

function labelled(label: string, value: string): DocRun[] {
  return [{ text: `${label}:`, bold: true }, { text: ` ${value}` }];
}

/** A labelled outline item, or a prompt item while the writer has not filled it in. */
function item(label: string, value: string, prompt: string): DocListItem {
  const trimmed = value.trim();
  return trimmed
    ? { text: labelled(label, trimmed) }
    : { text: prompt, prompt: true };
}

function mainPoints(input: SpeechOutlineInput): string[] {
  return input.points
    .map((point) => point.main.trim())
    .filter((main) => main !== "");
}

function header(
  input: SpeechOutlineInput,
  labels: SpeechOutlineLabels
): DocSection {
  const purposePrompt =
    input.type === "persuasive"
      ? labels.prompts.purposePersuasive
      : labels.prompts.purposeInformative;
  return {
    blocks: [
      fieldOrPrompt(labels.fields.topic, input.topic, labels.prompts.topic),
      fieldOrPrompt(labels.fields.purpose, input.purpose, purposePrompt),
      fieldOrPrompt(
        labels.fields.central,
        input.central,
        labels.prompts.central
      ),
    ],
  };
}

function introduction(
  input: SpeechOutlineInput,
  labels: SpeechOutlineLabels
): DocSection {
  const points = mainPoints(input);
  const previewPrompt =
    points.length > 0
      ? t(labels.prompts.previewWithPoints, { points: points.join("; ") })
      : labels.prompts.preview;
  return {
    heading: isMonroe(input)
      ? labels.headings.introductionMonroe
      : labels.headings.introduction,
    blocks: [
      {
        kind: "list",
        list: {
          style: "upper-roman",
          items: [
            item(
              labels.items.attention,
              input.attention,
              labels.prompts.attention
            ),
            item(
              labels.items.credibility,
              input.credibility,
              labels.prompts.credibility
            ),
            item(labels.items.preview, input.preview, previewPrompt),
          ],
        },
      },
    ],
  };
}

function supportItems(
  subs: string,
  labels: SpeechOutlineLabels
): DocListItem[] {
  const parsed = parseSubPoints(subs);
  if (parsed.length === 0) {
    return [{ text: labels.prompts.support, prompt: true }];
  }
  return parsed.map((sub) => ({
    text: sub.text,
    children:
      sub.details.length > 0
        ? {
            style: "decimal",
            items: sub.details.map((detail) => ({ text: detail })),
          }
        : undefined,
  }));
}

function mainItem(
  input: SpeechOutlineInput,
  labels: SpeechOutlineLabels,
  index: number
): DocListItem {
  const point = input.points[index] ?? BLANK_POINT;
  const main = point.main.trim();
  const step = isMonroe(input) ? MONROE_BODY_STEPS[index] : undefined;
  const children = {
    style: "upper-alpha" as const,
    items: supportItems(point.subs, labels),
  };
  if (step) {
    return main
      ? { text: labelled(labels.monroeSteps[step], main), children }
      : { text: labels.monroePrompts[step], prompt: true, children };
  }
  return main
    ? { text: main, children }
    : {
        text: t(labels.prompts.mainPoint, { n: index + 1 }),
        prompt: true,
        children,
      };
}

function transitionBlock(
  text: string,
  labels: SpeechOutlineLabels,
  index: number
): DocBlock {
  const trimmed = text.trim();
  if (trimmed === "") {
    return {
      kind: "prompt",
      text: t(labels.prompts.transition, {
        from: toRoman(index + 1),
        to: toRoman(index + 2),
      }),
    };
  }
  return {
    kind: "paragraph",
    text: [
      { text: "(" },
      { text: `${labels.items.transition}:`, italic: true },
      { text: ` ${trimmed})` },
    ],
  };
}

function body(
  input: SpeechOutlineInput,
  labels: SpeechOutlineLabels
): DocSection {
  // The Motivated Sequence always has its three body steps, filled in or not.
  const count = isMonroe(input)
    ? MONROE_BODY_STEPS.length
    : input.points.length;
  const blocks: DocBlock[] = [];
  for (let index = 0; index < count; index += 1) {
    blocks.push({
      kind: "list",
      list: {
        style: "upper-roman",
        start: index + 1,
        items: [mainItem(input, labels, index)],
      },
    });
    if (index < count - 1) {
      blocks.push(
        transitionBlock(
          (input.points[index] ?? BLANK_POINT).transition,
          labels,
          index
        )
      );
    }
  }
  return { heading: labels.headings.body, blocks };
}

function conclusion(
  input: SpeechOutlineInput,
  labels: SpeechOutlineLabels
): DocSection {
  const points = mainPoints(input);
  const summaryPrompt =
    points.length > 0
      ? t(labels.prompts.summaryWithPoints, { points: points.join("; ") })
      : labels.prompts.summary;
  const monroe = isMonroe(input);
  const items: DocListItem[] = [];
  // In the Motivated Sequence the summary is optional: Action is what must close it.
  if (!monroe || input.summary.trim() !== "") {
    items.push(item(labels.items.summary, input.summary, summaryPrompt));
  }
  if (monroe) {
    items.push(item(labels.items.action, input.action, labels.prompts.action));
  }
  items.push(item(labels.items.close, input.close, labels.prompts.close));
  return {
    heading: monroe
      ? labels.headings.conclusionMonroe
      : labels.headings.conclusion,
    blocks: [{ kind: "list", list: { style: "upper-roman", items } }],
  };
}

export function buildSpeechOutline(
  input: SpeechOutlineInput,
  labels: SpeechOutlineLabels
): DocumentModel {
  let subtitle = labels.subtitles.informative;
  if (isMonroe(input)) {
    subtitle = labels.subtitles.monroe;
  } else if (input.type === "persuasive") {
    subtitle = labels.subtitles.persuasive;
  }
  return {
    title: input.topic.trim() || labels.defaultTitle,
    subtitle,
    sections: [
      header(input, labels),
      introduction(input, labels),
      body(input, labels),
      conclusion(input, labels),
      {
        heading: labels.headings.references,
        blocks: [
          { kind: "guidance", text: labels.referencesNote },
          { kind: "prompt", text: labels.prompts.references },
        ],
      },
    ],
  };
}
